// `agentSpec` / `UiRender.catalog.spec` are opaque to the package; the host
// catalog renderer narrows them. We keep the inline-spec detector but treat
// the parsed value as `unknown`.
export type AgentSpec = unknown;

/**
 * Try to extract a JSON `AgentSpec` from message content.
 * Returns the parsed spec + any leading prose, or null.
 *
 * Uses a brace-balance walk (string-aware) to find the matching close of the
 * first `{` so trailing prose after the JSON doesn't poison the slice.
 */
export function tryExtractInlineAgentSpec(
  text: string,
): { spec: AgentSpec; preamble: string } | null {
  const start = text.indexOf('{');
  if (start === -1) return null;

  const end = findBalancedClose(text, start);
  if (end === -1) return null;

  try {
    const parsed: unknown = JSON.parse(text.slice(start, end + 1));
    if (
      parsed != null &&
      typeof parsed === 'object' &&
      'layout' in (parsed as Record<string, unknown>) &&
      'children' in (parsed as Record<string, unknown>) &&
      Array.isArray((parsed as Record<string, unknown>).children)
    ) {
      return {
        spec: parsed as AgentSpec,
        preamble: text.slice(0, start).trim(),
      };
    }
  } catch {
    // not valid JSON
  }
  return null;
}

function findBalancedClose(text: string, openIdx: number): number {
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = openIdx; i < text.length; i++) {
    const c = text[i];
    if (escape) {
      escape = false;
      continue;
    }
    if (inString) {
      if (c === '\\') escape = true;
      else if (c === '"') inString = false;
      continue;
    }
    if (c === '"') {
      inString = true;
      continue;
    }
    if (c === '{') depth++;
    else if (c === '}') {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}
